import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    const { dialog } = this.refs;
    if (dialog?.open) {
      dialog.close();
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
    }
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  /**
   * Only the primary drawer should react to CartAddEvent when auto-open is on.
   * A duplicate drawer (e.g. header + top bar) would otherwise both call showModal and fight for the top layer.
   */
  #shouldAutoOpenOnCartAdd() {
    if (!this.hasAttribute('auto-open')) return false;
    if (this.hasAttribute('data-primary-cart-drawer') || this.id === 'main-cart-drawer') return true;
    return document.querySelectorAll('cart-drawer-component').length === 1;
  }

  /**
   * `CartAddEvent` reuses the `cart:update` event name (see events.js). Theme and other
   * code also dispatch plain `CustomEvent('cart:update')` after AJAX add — those must NOT
   * open the drawer, or we get open → close (theme) → refresh → open flicker.
   */
  #handleCartAdd = (event) => {
    if (!(event instanceof CartAddEvent)) return;
    if (this.#shouldAutoOpenOnCartAdd()) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();
    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    return this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
